// Safe Delete (IA §34, backend §84): show impact, default to Archive, offer Replace references.
import { html, mount, toast } from '../lib/dom';
import { openModal } from './modal';
import { icon } from './icons';
import { assetThumb } from './thumb';
import { usage, listAssets, archiveAsset, deleteAsset, replaceReferences, ownerLabel } from '../api/library';
import { plural } from '../lib/format';

export function openSafeDelete(asset?: any, { onDone }: any = {}) {
  const u = usage(asset.id);
  if (u.loading) { toast(u.error ? 'Usage is unavailable. Reload before deleting.' : 'Checking asset usage. Please try again shortly.'); return; }
  const used = u.nodes > 0;
  openModal({
    title: `Delete “${asset.name}”?`,
    body: used
      ? html`<div class="banner error">${icon('alert')}<div><strong>This asset is used in:</strong><div class="mono" style="margin-top:6px;font-size:12px;line-height:1.7">${plural(u.flows, 'flow')}<br>${plural(u.nodes, 'node')}<br>${plural(u.projects, 'project')}</div></div></div>
        <div class="tree">${u.tree.map((p?: any) => html`<div class="l1">${p.project.name}</div>${p.flows.map((f?: any) => html`<div class="l2">└ ${f.flow.name}</div>${f.nodes.map((n?: any) => html`<div class="l3">└ ${n.node.name}</div>`)}`)}`)}</div>
        <p class="muted" style="font-size:13px">Archiving keeps existing flows working and hides the asset from pickers. Deleting breaks ${plural(u.nodes, 'node reference')}.</p>
        <div data-replace hidden class="col" style="gap:8px">
          <div class="field"><label>Replace all references with</label>
          <div class="grid auto-sm" data-replace-grid style="max-height:260px;overflow:auto"></div></div>
        </div><p class="banner error" role="alert" data-delete-error hidden></p>`
      : html`<p>This asset isn’t used by any flow. You can delete it safely, or archive it to keep version history.</p><p class="banner error" role="alert" data-delete-error hidden></p>`,
    foot: used
      ? html`<button class="btn ghost" data-close>Cancel</button><button class="btn" data-act="replace">${icon('replace')}Replace references</button><button class="btn danger" data-act="delete">${icon('trash')}Delete anyway</button><button class="btn primary" data-act="archive">${icon('archive')}Archive</button>`
      : html`<button class="btn ghost" data-close>Cancel</button><button class="btn" data-act="archive">${icon('archive')}Archive</button><button class="btn danger" data-act="delete">${icon('trash')}Delete</button>`,
    onMount(modal?: any, close?: any) {
      modal.addEventListener('click', async (e?: any) => {
        const act = e.target.closest('[data-act]')?.dataset.act;
        const pick = e.target.closest('[data-replace-with]')?.dataset.replaceWith;
        if (act === 'archive') { archiveAsset(asset.id); close(); toast('Asset archived — existing flows keep working'); onDone?.('archived'); }
        if (act === 'delete') {
          const button = e.target.closest('[data-act="delete"]');
          if (!button || button.disabled) return;
          const error = modal.querySelector('[data-delete-error]');
          button.disabled = true;
          error.hidden = true;
          try {
            await deleteAsset(asset.id);
            close();
            toast(used ? `Deleted. ${plural(u.nodes, 'node')} now need a new asset` : 'Asset deleted');
            onDone?.('deleted');
          } catch (cause: any) {
            error.textContent = cause?.message || 'Could not delete asset';
            error.hidden = false;
          } finally { button.disabled = false; }
        }
        if (act === 'replace') {
          const box = modal.querySelector('[data-replace]');
          box.hidden = false;
          const candidates = listAssets({ type: asset.type }).filter((a?: any) => a.id !== asset.id).slice(0, 18);
          mount(modal.querySelector('[data-replace-grid]'), candidates.map((a?: any) => html`<button class="card hover entity-card" data-replace-with="${a.id}" style="border:none;padding:0;text-align:left;cursor:pointer">${assetThumb(a)}<div class="body" style="padding:8px 10px"><span class="title ellipsis" style="font-size:12px">${a.name}</span><span class="meta">${ownerLabel(a)}</span></div></button>`));
          box.scrollIntoView({ block: 'nearest' });
        }
        if (pick) { const n = replaceReferences(asset.id, pick); archiveAsset(asset.id); close(); toast(`${plural(n, 'reference')} replaced, original archived`); onDone?.('replaced'); }
      });
    },
  });
}
